import {VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {ReactNode, useState} from 'react';

import {isNotNil, partition} from 'ramda';
import {isTrue} from 'ramda-adjunct';

import {SaleVehicleAlbumPhotoGuide} from '@dms/api/vehicle';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useSaleVehicleAlbum} from '../../../hooks/useSaleVehicleAlbum';
import {useGuidedAlbumDndManager} from '../hooks/useGuidedAlbumDndManager';
import {PhotoGuideActions} from '../hooks/usePhotoGuideActions';
import {PhotoGuideLayout} from '../types/PhotoGuideLayout';
import {RenderAlbumImage} from '../types/RenderAlbumImage';
import {UnifiedAlbumImage} from '../types/UnifiedAlbumImage';
import {buildPhotoGuideLayout} from '../utils/buildPhotoGuideLayout';
import {buildPhotoGuideLayoutContainerId} from '../utils/buildPhotoGuideLayoutContainerId';
import {isPromoTileImage} from '../utils/isPromoTileImage';
import {toPhotoGuideLayoutRequest} from '../utils/toPhotoGuideLayoutRequest';
import {PhotoBacklog} from './PhotoBacklog';
import {PhotoGuideSlotGrid} from './PhotoGuideSlotGrid';
import {PromoPhotoStrip} from './PromoPhotoStrip';

interface GuidedAlbumContentProps extends RequiredTestIdProps {
  saleVehicleAlbumId: string;
  photoGuide: SaleVehicleAlbumPhotoGuide;
  /** Album photos and promo tiles as the flat album would render them. */
  images: UnifiedAlbumImage[];
  inactiveContainerIds: string[];
  /** Photos selected in bulk mode across albums; a drag of one of them moves them all in. */
  selectedImageIds: string[];
  canEdit: boolean;
  /** The layout or the album photos are being refetched, so the two may not match yet. */
  isSyncing: boolean;
  /** When the layout and the album photos were last fetched (RTK's fulfilledTimeStamp). */
  fetchedAt: PhotoGuideFetchedAt;
  isLoading: boolean;
  isError: boolean;
  renderAlbumImage: RenderAlbumImage;
  /** Upload previews and the upload tile, rendered at the end of the Backlog. */
  backlogTrailingTiles: ReactNode;
  actions: Pick<PhotoGuideActions, 'assignPhotoType' | 'unassignPhoto' | 'updateLayout'>;
  /** The flat album's own moves: the Backlog order and photos dragged in from other albums. */
  albumActions: Pick<
    ReturnType<typeof useSaleVehicleAlbum>['actions'],
    'moveImages' | 'copyImages'
  >;
  albumPhotoCount: number;
  onAlbumChange?: () => void;
}

interface PhotoGuideFetchedAt {
  photoGuide: number | undefined;
  albumPhotos: number | undefined;
}

interface OptimisticPhotoGuideLayout {
  state: PhotoGuideLayout;
  isRequestPending: boolean;
  /** The fetches the optimistic layout was built over; it lasts until both have been redone. */
  basedOn: PhotoGuideFetchedAt;
}

/**
 * The Sales Album in Photo Guide mode: the Promo Photo strip, the Guide Layout and the Backlog.
 * A reorder is shown at once and kept while its request runs and while the layout and the photos
 * refetch afterwards, so the tiles neither jump back nor mix a new layout with old photos.
 */
export function GuidedAlbumContent(props: GuidedAlbumContentProps) {
  const [promoImages, albumImages] = partition(isPromoTileImage, props.images);
  const [optimistic, setOptimistic] = useState<OptimisticPhotoGuideLayout | null>(null);

  // After its request succeeded, the optimistic layout stays until both the layout and the photos
  // were fetched again; a refetch with unchanged data still moves the timestamp, so it never sticks.
  const isOptimisticCurrent =
    isNotNil(optimistic) &&
    (optimistic.isRequestPending ||
      optimistic.basedOn.photoGuide === props.fetchedAt.photoGuide ||
      optimistic.basedOn.albumPhotos === props.fetchedAt.albumPhotos);
  const state = isOptimisticCurrent
    ? optimistic.state
    : buildPhotoGuideLayout(props.photoGuide.items, albumImages);

  // Each optimistic layout settles only itself, so a slow first request cannot clear a newer one.
  const showOptimistic = (next: PhotoGuideLayout) => {
    const created: OptimisticPhotoGuideLayout = {
      state: next,
      isRequestPending: true,
      basedOn: props.fetchedAt,
    };
    setOptimistic(created);
    // A success waits for the refetch it triggers; a failure returns to the fetched state.
    return (isSuccess: boolean) =>
      setOptimistic((current) =>
        match({isReplaced: current !== created, isSuccess})
          .returnType<OptimisticPhotoGuideLayout | null>()
          .with({isReplaced: true}, () => current)
          .with({isSuccess: true}, () => ({...created, isRequestPending: false}))
          .otherwise(() => null)
      );
  };

  const layoutContainerId = buildPhotoGuideLayoutContainerId(props.saleVehicleAlbumId);
  // The album's existing move and copy resolve to `void` when they fail (useSaleVehicleAlbum).
  const handleAlbumActionResult = (result: boolean | void) => {
    const isSuccess = isTrue(result);
    if (isSuccess) {
      props.onAlbumChange?.();
    }
    return isSuccess;
  };
  const layoutPhotoCount = state.layout.filter((item) => isNotNil(item.image)).length;

  useGuidedAlbumDndManager({
    containerId: props.saleVehicleAlbumId,
    layoutContainerId,
    layout: state.layout,
    backlog: state.backlog,
    inactiveContainerIds: props.inactiveContainerIds,
    selectedImageIds: props.selectedImageIds,
    // A layout built while the layout and the photos resync could miss a photo, so drops wait.
    isEnabled: props.canEdit && !props.isSyncing,
    canMovePhotosIn: props.canEdit,
    onAssign: props.actions.assignPhotoType,
    onUnassign: props.actions.unassignPhoto,
    onUpdateLayout: (layout) => {
      const placedIds = new Set(layout.map((item) => item.image?.id));
      const settle = showOptimistic({
        layout,
        backlog: state.backlog.filter((image) => !placedIds.has(image.id)),
      });
      props.actions.updateLayout(toPhotoGuideLayoutRequest(layout)).then(settle);
    },
    // The Backlog keeps the album's own order, so a reorder is a plain photo move whose position
    // is the photo's index in the whole album: layout photos first, then the Backlog.
    onReorderBacklog: (backlog, movedImage) => {
      const settle = showOptimistic({layout: state.layout, backlog});
      props.albumActions
        .moveImages(props.saleVehicleAlbumId, [
          {fileId: movedImage.id, position: layoutPhotoCount + backlog.indexOf(movedImage)},
        ])
        .then(handleAlbumActionResult)
        .then(settle);
    },
    // Photos dragged in from another album join the end of the Backlog.
    onMovePhotosIn: (sourceAlbumId, photoIds, preferCopy) => {
      const albumAction = preferCopy
        ? props.albumActions.copyImages
        : props.albumActions.moveImages;
      albumAction(
        sourceAlbumId,
        photoIds.map((fileId, index) => ({fileId, position: props.albumPhotoCount + index}))
      ).then(handleAlbumActionResult);
    },
  });

  return (
    <VStack spacing={4}>
      <PromoPhotoStrip
        saleVehicleAlbumId={props.saleVehicleAlbumId}
        images={promoImages}
        renderAlbumImage={props.renderAlbumImage}
        data-testid={suffixTestId('promoPhotos', props)}
      />
      <PhotoGuideSlotGrid
        saleVehicleAlbumId={props.saleVehicleAlbumId}
        layoutContainerId={layoutContainerId}
        layout={state.layout}
        canEdit={props.canEdit}
        isLoading={props.isLoading}
        renderAlbumImage={props.renderAlbumImage}
        data-testid={suffixTestId('layout', props)}
      />
      <PhotoBacklog
        containerId={props.saleVehicleAlbumId}
        backlog={state.backlog}
        canEdit={props.canEdit}
        isLoading={props.isLoading}
        isError={props.isError}
        renderAlbumImage={props.renderAlbumImage}
        data-testid={suffixTestId('backlog', props)}
      >
        {props.backlogTrailingTiles}
      </PhotoBacklog>
    </VStack>
  );
}
